import React, { useState } from "react";
import { toast } from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { ICurrentData } from "./Sections";
import { ISectionData } from "@modules/sections/ISections";
import { Dimension } from "@models/IDimension";
import { SectionInfo } from "@models/ISchema";
import { EmptyResults, Header } from "@common/components";
import Layout from "@common/layout/Layout";
import Sections from "@modules/sections/Sections";
import useGetDimension from "@dimensions/hooks/useGetDimension";
import DeleteSectionModal from "../delete-section-modal";
import useContextUrl from "@common/hooks/useContextUrl";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import ChangeComponentDrawer from "../change-component-drawer";

interface ISectionActionProps {
  current: ICurrentData;
  calendarData: any;
  headerData: any;
  loading: boolean;
  error: boolean;
  sectionInfo?: SectionInfo | null;
  setDataCalendar: (data: ISectionData, callback: () => void) => void;
  setCurrentData: (value: any) => void;
  onRefresh?: () => void;
}

const SectionAction: React.FC<ISectionActionProps> = (props: ISectionActionProps) => {
  const {
    current,
    calendarData,
    headerData,
    loading,
    sectionInfo,
    setDataCalendar,
    setCurrentData,
    error,
    onRefresh,
  } = props;

  const history = useHistory();
  const { origin, scenario, workspace } = useContextUrl();
  const { t } = useTranslation();
  const [activeSession, setActiveSession] = useState(false);
  const contextUrl = `${workspace}/${scenario}/${origin}`;
  const { adaptedHomeUrl } = useNavigationUrl();
  const [dimension] = useGetDimension(Dimension.section);
  const [isChangeComponentOpen, setIsChangeComponentOpen] = useState(false);

  const handleOpenEdit = () => {
    const linkId = current?.links?.[0]?.id;
    history.push(`/editor/form-edit/${contextUrl}/${linkId}}`, {
      sectionId: current.sectionId,
      createSession: true,
    });
  };

  const showSkeleton =
    (loading || !calendarData || !headerData || !sectionInfo || !current) && !error;

  return (
    <Layout contextSearch>
      {!error && (
        <Header
          isLoading={
            showSkeleton
              ? {
                  topTitle: true,
                  title: true,
                  subTitle: true,
                  moreDetails: true,
                }
              : undefined
          }
          dimension={dimension.label}
          {...(headerData ?? {})}
          callbackOpenEdit={handleOpenEdit}
          callbackOpenDisabledSession={(value: boolean) => {
            setActiveSession(value);
          }}
          callbackOpenChangeComponent={setIsChangeComponentOpen}
          onShowError={() =>
            toast.error(t("dimension.section.delete-section.error-message"), {
              autoClose: 5000,
            })
          }
          breadcrumbItems={[
            {
              path: adaptedHomeUrl(),
              title: t("dimension.section.breadcrumb.previous"),
            },
            {
              title: t("dimension.section.breadcrumb.current"),
            },
          ]}
          shouldRenderSectionMenu={!isChangeComponentOpen}
        />
      )}

      {error && <EmptyResults dimension="Sección" isCalendarView />}

      <DeleteSectionModal isOpen={activeSession} onCancel={() => setActiveSession(false)} />

      {!error && (
        <Sections
          isLoading={showSkeleton}
          config={{
            scenarioId: parseInt(scenario),
            originId: parseInt(origin),
            workspaceId: parseInt(workspace),
          }}
          data={calendarData}
          setDataGrid={setDataCalendar}
          currentWeek={current?.week}
          setCurrentWeek={(value: any) => {
            const cloneCurrent = { ...current };
            setCurrentData({ ...cloneCurrent, week: value });
          }}
        />
      )}

      <ChangeComponentDrawer
        isOpen={isChangeComponentOpen}
        component={current?.component}
        sectionId={current?.sectionId}
        courseId={current?.courseId}
        courseComponent={current?.courseComponent}
        onClose={() => setIsChangeComponentOpen(false)}
        onSuccess={onRefresh}
      />
    </Layout>
  );
};

export default SectionAction;
